import { useCustomerCtx, useDialogCtx } from '../../../hooks/context-hooks'
import { endpoints } from '../../../api/form-schema'
import { useTranslation } from 'react-i18next'
import { Box, useTheme, Card } from '@mui/material'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import GenericTable from 'src/components/table/GenericTable'
import { useState, useMemo, useCallback } from 'react'
import { IFilledFormSchemaReport } from 'src/generated-types/filled-form-schema-report'
import { formSchemaQueryKeys } from 'src/query-keys'
import { useGet } from 'src/api/reactQuery'
import FormSchemaReportDetail from './FormSchemaReportDetail'
import { getFormattedDate } from 'src/utils'
import { SchemaType } from 'src/generated-types/form-schema'

const FormSchemaReportsScreen = () => {
  const { showDialog } = useDialogCtx()
  const { customer } = useCustomerCtx()
  const { t: translate, i18n } = useTranslation(['common', 'origin-names'])
  const theme = useTheme()

  const [selectedLocalityId, setSelectedLocalityId] = useState<number | null>(null)
  const [selectedLocalityGroupId, setSelectedLocalityGroupId] = useState<number | null>(null)
  const [selectedFormSchemaId, setSelectedFormSchemaId] = useState<number | null>(null)

  const customerId = customer?.id as EntityId

  const { data: filterOptions } = useGet<{
    localities: Array<{ id: number; name: string }>
    localityGroups: Array<{ id: number; name: string }>
    schemas: Array<{ id: number; name: string }>
  }>(endpoints.getFilledFormSchemaReportsFilterOptions(customerId))

  const queryParams = useMemo(
    () => ({
      schemaType: SchemaType.FormSchema,
      ...(selectedLocalityId && { localityId: selectedLocalityId }),
      ...(selectedLocalityGroupId && { localityGroupId: selectedLocalityGroupId }),
      ...(selectedFormSchemaId && { formSchemaId: selectedFormSchemaId }),
    }),
    [selectedLocalityId, selectedLocalityGroupId, selectedFormSchemaId],
  )

  const generateQueryKey = useCallback(
    (page: number, pageSize: number) =>
      formSchemaQueryKeys.GET_FILLED_FORM_SCHEMA_REPORTS(customerId, { ...queryParams, page, pageSize }),
    [customerId, queryParams],
  )

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: '#',
        flex: 0.5,
        minWidth: 70,
      },
      {
        field: 'formSchemaName',
        headerName: translate('form-schema', { ns: 'common' }),
        flex: 3,
      },
      {
        field: 'linkedEntityName',
        headerName: translate('linked-entity', { ns: 'common' }),
        flex: 3,
        renderCell: (params: any) =>
          `${params.row.linkedEntityName} | ${translate(params.row.linkedEntityType, { ns: 'origin-names' })}`,
      },
      {
        field: 'localityName',
        headerName: translate('locality', { ns: 'common' }),
        flex: 2,
      },
      {
        field: 'filledDate',
        headerName: translate('filled-date', { ns: 'common' }),
        flex: 2,
        renderCell: (params: any) => {
          const date = new Date(params.row.filledDate)
          return getFormattedDate(date)
        },
      },
      {
        field: 'createdByUserName',
        headerName: translate('created-by', { ns: 'common' }),
        flex: 2,
      },
    ],
    [translate, i18n.language],
  )

  const handleRowClick = (row: IFilledFormSchemaReport) => {
    showDialog(
      FormSchemaReportDetail,
      {
        componentProps: {
          report: row,
          customerId: customerId,
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
          disableEscapeKeyDown: false,
        },
      },
      undefined,
      true,
    )
  }

  return (
    <CommonPageLayout
      titleSection={{
        title: translate('form-schema-reports', { ns: 'common' }),
        subtitle: translate('form-schema-reports-desc', { ns: 'common' }),
      }}
    >
      <Card sx={{ padding: 2, marginBottom: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Box flex={1} minWidth={200}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {translate('locality', { ns: 'common' })}
            </label>
            <select
              value={selectedLocalityId || ''}
              onChange={e => {
                setSelectedLocalityId(e.target.value ? parseInt(e.target.value) : null)
              }}
              style={{
                width: '100%',
                padding: '8px',
                marginTop: '4px',
                borderRadius: '4px',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <option value="">All</option>
              {filterOptions?.localities?.map(locality => (
                <option key={locality.id} value={locality.id}>
                  {locality.name}
                </option>
              ))}
            </select>
          </Box>

          <Box flex={1} minWidth={200}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {translate('locality-group', { ns: 'common' })}
            </label>
            <select
              value={selectedLocalityGroupId || ''}
              onChange={e => {
                setSelectedLocalityGroupId(e.target.value ? parseInt(e.target.value) : null)
              }}
              style={{
                width: '100%',
                padding: '8px',
                marginTop: '4px',
                borderRadius: '4px',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <option value="">All</option>
              {filterOptions?.localityGroups?.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </Box>

          <Box flex={1} minWidth={200}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {translate('form-schema', { ns: 'common' })}
            </label>
            <select
              value={selectedFormSchemaId || ''}
              onChange={e => {
                setSelectedFormSchemaId(e.target.value ? parseInt(e.target.value) : null)
              }}
              style={{
                width: '100%',
                padding: '8px',
                marginTop: '4px',
                borderRadius: '4px',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <option value="">All</option>
              {filterOptions?.schemas?.map(schema => (
                <option key={schema.id} value={schema.id}>
                  {schema.name}
                </option>
              ))}
            </select>
          </Box>
        </Box>
      </Card>

      <GenericTable
        id="filled-form-schemas-table"
        columns={columns}
        enableServerPagination
        enablePagination
        queryUrl={endpoints.getFilledFormSchemaReports(customerId)}
        queryParams={queryParams}
        generateQueryKey={generateQueryKey}
        pageSize={50}
        pageOptions={[50, 100, 200]}
        getRowId={row => row.id}
        onRowClick={({ row }) => handleRowClick(row)}
        getRowHeight={() => 44}
        hideDefaultFooterPagination
      />
    </CommonPageLayout>
  )
}

export default FormSchemaReportsScreen
